import { useState } from 'react'
import { Link } from 'react-router-dom'
import { trackInternalEvent } from '@rio/tracking'

import { useI18n } from 'builder/hooks/useI18n'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { useAutoApply } from 'builder/views/AutoApply/hooks/useAutoApply'

import Button, { ButtonTheme } from 'builder/components/Button'
import { Icon20, Icon24 } from 'builder/components/Icon'
import { useThemeConfig } from '../../hooks/useThemeConfig'
import { WidgetProps } from '../../widget.types'
import { WidgetTheme } from '../../WidgetTheme'
import { Service } from '../../../types'
import { WidgetBox, WidgetImage } from '../../widget.styles'
import { InfoBox } from '../../elements/InfoBox/InfoBox'
import { TRANSLATION } from '../../constants'
import { SuccessBox } from '../../elements/SuccessBox/SuccessBox'
import { ActionButton, ActionText, Box, CloseButton } from './styles'
import JobRecomandationImg from './assets/job-recomandation.png'
import JobRecomandationMobileImg from './assets/job-recomandation-mobile.png'

import Banner from './assets/banner.png'

const localStorageKey = 'jobs_applied_banner'

function useJobsAppliedBanner() {
  const [jobsAppliedBanner, setJobsAppliedBanner] = useState(() => {
    const stored = localStorage.getItem(localStorageKey)
    return stored ? JSON.parse(stored) : true
  })

  const toggleBanner = () => {
    const newValue = !jobsAppliedBanner
    setJobsAppliedBanner(newValue)
    localStorage.setItem(localStorageKey, JSON.stringify(newValue))
  }

  return { jobsAppliedBanner, toggleBanner }
}

function InitialJobApplied(props: {
  toggleBanner: () => void
  handleAutoApplyClick: (e: React.MouseEvent) => void
  text: {
    badge?: string
    header: string
    subHeader: string
    applyForMe: string
    notNow: string
  }
}) {
  const onClose = () => {
    props.toggleBanner()
    trackInternalEvent('progress_promo_close', { type: 'auto-apply' })
  }

  const onAutoApplyClick = (e: React.MouseEvent) => {
    trackInternalEvent('progress_promo_cta_click', {
      type: 'auto-apply',
      cta: props.text.applyForMe,
    })
    props.handleAutoApplyClick(e)
  }
  return (
    <>
      <CloseButton onClick={onClose}>
        <Icon24.Close />
      </CloseButton>
      <WidgetBox>
        <InfoBox text={props.text}>
          <Box display="flex" gap="12px" alignItems="flex-end">
            <Button
              as={Link}
              to="/auto-apply/start"
              theme={ButtonTheme.alternative}
              onClick={onAutoApplyClick}
            >
              {props.text.applyForMe}
            </Button>
            <Button theme={ButtonTheme.ghost} onClick={onClose}>
              {props.text.notNow}
            </Button>
          </Box>
        </InfoBox>
      </WidgetBox>
      <WidgetBox>
        <WidgetImage src={Banner} alt="Banner" />
      </WidgetBox>
    </>
  )
}

function ProgressJobApplied(props: {
  service: Service
  isUserOnAutoApplyPlan: boolean
  handleAutoApplyClick: (e: React.MouseEvent) => void
  text: {
    badge?: string
    header: string
    subHeader: string
    applyForMe: string
    exploreJobs: string
  }
}) {
  const { isPhone } = useMediaQueries()

  const handleClick = (autoApply: boolean) => {
    trackInternalEvent('progress_cta_click', {
      score: props.service.title,
      cta: autoApply ? props.text.applyForMe : props.text.exploreJobs,
    })
  }

  return (
    <>
      <WidgetBox>
        <InfoBox text={props.text}>
          <Box display="flex" gap="4px" flexDirection="column" directionOnMobile="row">
            <ActionButton
              as={Link}
              to="/job-search?view=recommendation"
              style={{ background: 'white', display: 'block', padding: 11, borderRadius: 8 }}
              theme={ButtonTheme.ghost}
              onClick={() => handleClick(false)}
            >
              <ActionText>
                {props.text.exploreJobs}
                <Icon20.ChevronRightRegular />
              </ActionText>
            </ActionButton>
            {props.isUserOnAutoApplyPlan && (
              <ActionButton
                as={Link}
                to="/auto-apply/start"
                style={{ background: 'white', display: 'block', padding: 11, borderRadius: 8 }}
                theme={ButtonTheme.ghost}
                onClick={(e: React.MouseEvent) => {
                  handleClick(true)
                  props.handleAutoApplyClick(e)
                }}
              >
                <ActionText>
                  {props.text.applyForMe}
                  <Icon20.ChevronRightRegular />
                </ActionText>
              </ActionButton>
            )}
          </Box>
        </InfoBox>
      </WidgetBox>
      <WidgetBox>
        <WidgetImage
          src={isPhone ? JobRecomandationMobileImg : JobRecomandationImg}
          style={{ margin: 'auto' }}
        />
      </WidgetBox>
    </>
  )
}

/**
 * This widget is used to show the Jobs Applied section in the dashboard.
 * ATM is not complete since it needs another success screen state, but that
 * will be implmented once the backend is ready.
 */
export const JobApplied = ({ service }: WidgetProps) => {
  const { i18n } = useI18n()
  const { isPhone } = useMediaQueries()

  const autoApply = useAutoApply()
  const themeState = useThemeConfig({ service })
  const { jobsAppliedBanner, toggleBanner } = useJobsAppliedBanner()
  const theme = themeState.initial
  const progress = service.calculatedProgress?.percentageProgress || 0

  const initialText = {
    badge: i18n.t(`${TRANSLATION}.job_applied.initial.badge`),
    header: i18n.t(`${TRANSLATION}.job_applied.initial.header`),
    subHeader: i18n.t(`${TRANSLATION}.job_applied.initial.sub_header`),
    applyForMe: i18n.t(`${TRANSLATION}.job_applied.initial.apply_for_me`),
    notNow: i18n.t(`${TRANSLATION}.job_applied.initial.not_now`),
  }

  const progressText = {
    badge: i18n.t(`${TRANSLATION}.job_applied.progress.badge`),
    header: i18n.t(`${TRANSLATION}.job_applied.progress.header`),
    subHeader: i18n.t(`${TRANSLATION}.job_applied.progress.sub_header`),
    applyForMe: i18n.t(`${TRANSLATION}.job_applied.progress.apply_for_me`),
    exploreJobs: isPhone
      ? i18n.t(`${TRANSLATION}.job_applied.progress.explore_jobs_mobile`)
      : i18n.t(`${TRANSLATION}.job_applied.progress.explore_jobs`),
  }

  const completedText = {
    header: i18n.t(`${TRANSLATION}.job_applied.completed.header`),
    subHeader: i18n.t(`${TRANSLATION}.job_applied.completed.sub_header`),
    actionText: i18n.t(`${TRANSLATION}.job_applied.completed.action_text`),
  }

  const handleAutoApplyClick = (e: React.MouseEvent) => {
    if (!autoApply.isUserOnAutoApplyPlan) {
      e.preventDefault()
      autoApply.redirectToBillingPlans()
    }
  }

  if (progress >= 100) {
    trackInternalEvent('progress_score_completed', { score: service.title })

    return (
      <WidgetTheme theme={themeState.completed} tipOrder={service.widgetTipOrder}>
        <WidgetBox>
          <InfoBox text={completedText} isCompleted={true}>
            <ActionButton as={Link} to={`/${service?.getNextActionUrl() ?? '#'}`}>
              {completedText.actionText}
            </ActionButton>
          </InfoBox>
        </WidgetBox>
        <WidgetBox>
          <SuccessBox />
        </WidgetBox>
      </WidgetTheme>
    )
  }

  return (
    <WidgetTheme theme={theme} tipOrder={service.widgetTipOrder}>
      {jobsAppliedBanner && autoApply.isUserOnAutoApplyPlan ? (
        <InitialJobApplied
          text={initialText}
          toggleBanner={toggleBanner}
          handleAutoApplyClick={handleAutoApplyClick}
        />
      ) : (
        <ProgressJobApplied
          service={service}
          handleAutoApplyClick={handleAutoApplyClick}
          text={progressText}
          isUserOnAutoApplyPlan={autoApply.isUserOnAutoApplyPlan}
        />
      )}
    </WidgetTheme>
  )
}
